import React, {useEffect, useId, useRef, useState} from "react";
import {Skeleton} from "antd";
import {useTranslation} from "../../../i18n/I18nProvider";
import {LOADING_MESSAGE, subscribeToKey, unsubscribeFromKey} from "../../../i18n/LiveDictionary";
import {localeStringFadeStyle, localeStringInjectKeyFrames} from "../../../i18n/StyleHelper";
import {shallowEqual} from "react-redux";
import {wrapText} from "helpers/stringHelper";

type Props = {
    value: string;
    formatValue?: Record<string, any>;
    maxLineWidth?: number; // wrap için karakter limiti
    className?: string;
    style?: React.CSSProperties;
    loadingFallback?: React.ReactNode;
};

const LocaleStringWrapped = (props: Props) => {
    const { value, formatValue, style, className, loadingFallback, maxLineWidth = 60 } = props;
    const { t, tlocale, mhash } = useTranslation();
    const subscriberId = useId();
    const [text, setText] = useState(() => t(value, formatValue));
    const prevValueRef = useRef<string>();

    useEffect(() => {
        localeStringInjectKeyFrames();
    }, []);

    useEffect(() => {
        const updateText = () => setText(t(value, formatValue));

        if (prevValueRef.current && prevValueRef.current !== value) {
            unsubscribeFromKey(prevValueRef.current, subscriberId);
        }

        subscribeToKey(value, subscriberId, updateText);
        prevValueRef.current = value;

        updateText();

        return () => unsubscribeFromKey(value, subscriberId);
    }, [value, formatValue, tlocale, mhash, t, subscriberId]);

    const isLoading = text === LOADING_MESSAGE;

    const segments = !isLoading
        ? (text?.includes("\r\n")
            ? text.split("\r\n")
            : wrapText(text ?? "", maxLineWidth))
        : [];

    return (
        <span
            style={{ ...style, ...(isLoading ? {} : localeStringFadeStyle) }}
            className={className}
            title={isLoading ? undefined : text}
            lang={tlocale}
        >
      {isLoading ? (
          loadingFallback ?? (
              <Skeleton
                  title={false}
                  paragraph={{ rows: 1, width: "80%" }}
                  active
                  style={{ display: "inline-block", margin: 0 }}
              />
          )
      ) : (
          <>
              {segments.map((s, i) => (
                  <p key={i} className="mino-line">{s}</p>
              ))}
          </>
      )}
    </span>
    );
};

export default React.memo(LocaleStringWrapped, (prev, next) => {
    return (
        prev.value === next.value &&
        prev.maxLineWidth === next.maxLineWidth &&
        prev.formatValue === next.formatValue &&
        prev.className === next.className &&
        prev.loadingFallback === next.loadingFallback &&
        shallowEqual(prev.style, next.style)
    );
});
